import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar } from 'components';
import { Tooltip } from 'components-antd';
import { showErrorMessage } from 'helpers/errors';
import { CLIENT, TEAM_ADMIN, TEAM_OWNER, TEAM_BASIC } from 'settings/constants/roles';
import { postReinviteClientById } from 'api/clientsList';
import { Resend, Copy, Message } from 'components/Icons';
import { getConnectionTypeSelector } from 'store/selectors/app';
import { AGENT_CONNECTIONS, DRAWER_MESSAGES_TYPES } from 'settings/constants/drawers';
import { setDrawerAgentProfileId } from 'store/effects';
import styles from './styles.module.scss';
import { postReinviteTeamMember } from 'api/teamList';
import { useHistory } from 'react-router-dom';
import { routes } from 'settings/navigation/routes';
import { convertNameToAvatarPlaceholder, getIfExistThread, showSuccessMessage } from 'helpers';
import Send from 'components/Icons/Send';
import { setClientsListSendDateAction, setTeamListSendDateAction } from 'store/actions/clientsList';
import { getAgentTeamRoleSelector } from 'store/selectors/agentTeamDetail';
import { getUserId } from 'store/selectors/user';
import { getThreadsListSelector } from 'store/selectors/sockets/threads';
import { SOCKET_THREAD_TYPES } from 'settings/constants/sockets';
import {
  changeMessagesDrawerTypeAction,
  openMessagesDrawerAction,
} from 'store/actions/drawers/messages';
import {
  appDrawerSetClientIdAndInstancesAction,
  appManageClientDrawerAction,
  appSetClientsSearchValueAction,
} from 'store/actions/app';
import moment from 'moment';

const ListItem = ({
  testid,
  connectionName,
  connectionRole,
  teamRole,
  IsManagedByCurrentUser,
  connectionId,
  searchInstances,
  transactionInstances,
  listType,
  avatarUrl,
  inviteLink,
  clientTags,
  sendInvitation,
  sendDate,
}) => {
  const connectionType = useSelector(getConnectionTypeSelector);
  const [reinviting, setReinviting] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const agentRole = useSelector(getAgentTeamRoleSelector);
  const currentUserId = useSelector(getUserId);
  const threads = useSelector(getThreadsListSelector);
  const [invited, setInvited] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleCopy = () => {
    if (reinviting) return;
    navigator.clipboard.writeText(inviteLink);
    setShowTooltip(true);
    setTimeout(() => setShowTooltip(false), 1000);
  };

  const additionalInfoClassName = classNames(
    styles.additionalInfoContainer,
    {
      [styles.pendingAdditionalInfoContainer]: listType === 'Pending',
    },
    { [styles.additionalInfoContainerDisabled]: reinviting },
  );

  const resendInvite = useCallback(async () => {
    if (reinviting) return;
    try {
      setReinviting(true);
      if (connectionRole === CLIENT) {
        await postReinviteClientById({ id: connectionId });
        dispatch(setClientsListSendDateAction(connectionId));
      } else if (connectionType === AGENT_CONNECTIONS.Team) {
        await postReinviteTeamMember({ id: connectionId, useExistingToken: true });
        dispatch(setTeamListSendDateAction(connectionId));
      }
      showSuccessMessage(`${connectionRole === CLIENT ? 'Client' : 'Team member'} reinvited.`);
      if (!sendInvitation) setInvited(true);
    } catch (err) {
      showErrorMessage(
        `Unable to reinvite ${connectionRole === CLIENT ? 'client' : 'team member'}.`,
      );
    }
    setReinviting(false);
  }, [setReinviting, connectionRole, connectionId]);

  const getPendingActions = useCallback(
    () => (
      <div testid="resend" className={classNames(styles.button, styles.flex)}>
        {
          <span className={classNames(styles.tooltip, { [styles.show]: showTooltip })}>
            {`Invitation link copied for ${connectionName}.`}
          </span>
        }
        {inviteLink && (
          <Tooltip
            placement="top"
            title={'Copy Invite Link'}
            overlayClassName={classNames(styles.tooltipSend, 'mosaikTooltip')}
          >
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleCopy();
              }}
            >
              <Copy color={'#515151'} size={'20px'} />
            </span>
          </Tooltip>
        )}
        <Tooltip
          placement="top"
          title={`${sendInvitation ? 'Resend Invite' : 'Send Invite'}`}
          overlayClassName={classNames(styles.tooltipSend, 'mosaikTooltip')}
        >
          <span
            onClick={(e) => {
              e.stopPropagation();
              resendInvite();
            }}
          >
            {sendInvitation || invited ? (
              <Resend color={'#515151'} size={'20px'} />
            ) : (
              <Send color={'#515151'} size={'20px'} />
            )}
          </span>
        </Tooltip>
      </div>
    ),
    [reinviting, resendInvite, sendInvitation, showTooltip],
  );

  const handleDrawerClose = useCallback(() => {
    dispatch(appManageClientDrawerAction(false));
    dispatch(appSetClientsSearchValueAction(''));
  }, [dispatch]);

  const handleItemClick = () => {
    if (connectionType === AGENT_CONNECTIONS.Collaborators) return;
    if (connectionType === AGENT_CONNECTIONS.Clients) {
      handleDrawerClose();
      history.push(`${routes.clientProfile}/${connectionId}`);
    } else {
      const canViewProfile =
        agentRole !== TEAM_BASIC && IsManagedByCurrentUser && connectionId !== currentUserId;

      if (canViewProfile) {
        handleDrawerClose();
        history.push(`${routes.memberProfile}/${connectionId}`);
      } else
        dispatch(
          connectionType === AGENT_CONNECTIONS.Team
            ? setDrawerAgentProfileId({ id: connectionId, open: false, showExpandedSection: true })
            : appDrawerSetClientIdAndInstancesAction({
                id: connectionId,
                searchInstances,
                transactionInstances,
                open: false,
                showExpandedSection: true,
              }),
        );
    }
  };

  const handleMessageClick = () => {
    const existThread = getIfExistThread(
      threads,
      [connectionId, currentUserId],
      (thread) => thread?.Type === SOCKET_THREAD_TYPES.CHAT,
    );
    const participantObj = {
      id: connectionId,
      name: connectionName,
      value: connectionId,
      avatarUrl: avatarUrl,
    };
    dispatch(openMessagesDrawerAction(true));
    dispatch(
      changeMessagesDrawerTypeAction({
        type: existThread ? DRAWER_MESSAGES_TYPES.CHAT : DRAWER_MESSAGES_TYPES.NEW_MESSAGE,
        params: {
          threadId: existThread?.Id || null,
          participants: [participantObj],
        },
      }),
    );
    handleDrawerClose();
  };

  const getRegularActions = () => {
    return (
      <div testid="message" className={styles.regularActions}>
        {connectionType === AGENT_CONNECTIONS.Team &&
        (teamRole === TEAM_ADMIN || teamRole === TEAM_OWNER) ? (
          <div className={styles.tagContainer}>
            <div className={styles.tag}>{teamRole}</div>
          </div>
        ) : null}
        <span
          onClick={(e) => {
            e.stopPropagation();
            handleMessageClick();
          }}
        >
          <Message fill={'#515151'} />
        </span>
      </div>
    );
  };

  const isStartStyled = () => {
    if (connectionType !== AGENT_CONNECTIONS.Clients) return true;

    if (
      connectionType === AGENT_CONNECTIONS.Clients &&
      sendInvitation &&
      sendDate &&
      listType === 'Pending'
    )
      return true;

    return false;
  };

  const showLastInvitedDate = () => {
    if (listType !== 'Pending') return false;

    if (connectionType !== AGENT_CONNECTIONS.Collaborators && sendInvitation) return true;

    return false;
  };

  return (
    <div
      testid={testid}
      className={styles.itemContainer}
      onClick={() => {
        handleItemClick(false);
      }}
    >
      <div className={styles.connectionDetails}>
        <div
          className={classNames(styles.connectionNameContainer, {
            [styles.connectionNameContainerStart]: isStartStyled(),
          })}
        >
          <Avatar
            src={avatarUrl}
            avatarClassName={styles.avatar}
            placeholder={convertNameToAvatarPlaceholder(connectionName)}
          />
          <div className={styles.connectionInfo}>
            <div className={styles.connectionMeta}>
              <p testid="name" className={styles.connectionName}>
                {connectionName}
              </p>
              {connectionType !== AGENT_CONNECTIONS.Clients && !showLastInvitedDate() ? (
                <div className={styles.connectionRole}>{connectionRole}</div>
              ) : null}
              {showLastInvitedDate() && (
                <div className={styles.connectionRole}>{`Last Sent: ${
                  sendDate ? `${moment(sendDate).format('M/D/YY')}` : '-'
                }`}</div>
              )}
            </div>
          </div>
        </div>
        <div testid="additional_info" className={additionalInfoClassName}>
          {listType !== 'Pending' && getRegularActions()}
          {listType === 'Pending' && connectionType !== AGENT_CONNECTIONS.Collaborators && (
            <div testid="send_status">{getPendingActions()}</div>
          )}
        </div>
      </div>
      {connectionType === AGENT_CONNECTIONS.Clients && clientTags?.length ? (
        <div className={styles.clientTags}>
          {clientTags.map((tag) => (
            <div className={styles.tag}>{tag.TagName}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

ListItem.propTypes = {
  connectionName: PropTypes.string,
  connectionRole: PropTypes.string,
  teamRole: PropTypes.string,
  activeTabName: PropTypes.string,
  sendDate: PropTypes.string,
  connectionId: PropTypes.number,
  testid: PropTypes.string,
  IsManagedByCurrentUser: PropTypes.bool,
};

ListItem.defaultProps = {
  connectionName: '',
  connectionRole: '',
  teamRole: '',
  activeTabName: '',
  sendDate: '',
  connectionId: null,
  testid: undefined,
  IsManagedByCurrentUser: false,
};

export default ListItem;
